import { ModuleDTO, ModuleDTOWrapper } from "../../shared/DTO/ModuleDTO_new";
import {
  DeviceTypesMap,
  GROUP,
  DeviceType,
  CONNECTIVITY_STATE,
  STATE_VAR,
  ClimatizationDeviceTypesMap,
  ClimatizationDeviceType,
} from "./devices.constant";
import { SharedUtils } from "../../shared/shared.utils";

export class Device {
  SharedUtils = SharedUtils;
  constructor(
    private moduleId: string,
    private model: string,
    private vendor: string,
    private creationDate: Date,
    private moduleLocalId: string,
    private connectivityState: CONNECTIVITY_STATE,
    private name: string,
    private serialNumber: string,
    private favorite: number,
    private deviceId: string,
    private firmwareVersion: string,
    private houseId: string,
    private lastCommunication: string,
    private groups: Set<GROUP>,
    private stateVars: Map<string, any>,
    private userAttributes: Map<string, any>,
    private historicVars: Map<string, any>,
    private iconUrl?: string
  ) {}

  static fromDTOWrapper(moduleDTOWrapper: ModuleDTOWrapper): Device[] {
    const devices: Device[] = [];
    if (moduleDTOWrapper.Modules) {
      moduleDTOWrapper.Modules.forEach((module) => {
        devices.push(this.fromDTO(module));
      });
    }
    return devices;
  }

  static fromDTO(moduleDTO: ModuleDTO): Device {
    const groups: Set<GROUP> = new Set<GROUP>();
    if (moduleDTO.groups) {
      moduleDTO.groups.forEach((g) => groups.add(GROUP[g]));
    }

    const device = new Device(
      moduleDTO.moduleId,
      moduleDTO.model,
      moduleDTO.vendor,
      moduleDTO.creationDate,
      moduleDTO.moduleLocalId,
      <CONNECTIVITY_STATE>moduleDTO.connectivityState,
      moduleDTO.name,
      moduleDTO.serialNumber,
      moduleDTO.favorite,
      moduleDTO.deviceId,
      moduleDTO.firmwareVersion,
      moduleDTO.houseId,
      moduleDTO.lastCommunication,
      groups,
      moduleDTO.stateVars,
      moduleDTO.userAttributes,
      moduleDTO.historicVars
    );

    // device.getSupportedStateVars()
    //     .filter(sv => sv !== STATE_VAR.SUPPORTED)
    //     .forEach(sv => {
    //         device.setStateVar(sv, undefined);
    //     });

    return device;
  }

  static extractTypes(devices: Device[]): DeviceType[] {
    const types = new Set<DeviceType>();
    devices.forEach((device) => {
      if (types.size === 4) {
        return this.sortTypes(Array.from(types));
      } else {
        DeviceTypesMap.map.forEach((value, key) => {
          if (
            value.groups.some((v) => device.getGroups().has(v)) &&
            value.nonGroups.every((v) => !device.getGroups().has(v))
          ) {
            types.add(key);
          }
        });
      }
    });
    return this.sortTypes(Array.from(types));
  }

  private static sortTypes(types: DeviceType[]): DeviceType[] {
    const orderedTypes = types.sort((v1, v2) => {
      const order1 = DeviceTypesMap.map.get(v1).order;
      const order2 = DeviceTypesMap.map.get(v2).order;
      if (order1 < order2) {
        return -1;
      } else if (order1 > order2) {
        return 1;
      } else {
        return 0;
      }
    });
    return orderedTypes;
  }

  getModuleId(): string {
    return this.moduleId;
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  getModuleLocalId(): string {
    return this.moduleLocalId;
  }

  getName(): string {
    return this.name;
  }

  getConnectivityState(): CONNECTIVITY_STATE {
    return this.connectivityState;
  }

  setConnectivityState(connectivityState: CONNECTIVITY_STATE): void {
    this.connectivityState = connectivityState;
  }

  isOnline(): boolean {
    return this.connectivityState === CONNECTIVITY_STATE.ONLINE;
  }

  isOffline(): boolean {
    return this.connectivityState === CONNECTIVITY_STATE.OFFLINE;
  }

  getGroups(): Set<GROUP> {
    return this.groups;
  }

  getStateVar(stateVar: string): any {
    return this.stateVars[stateVar];
  }

  setStateVar(stateVar: string, value: any) {
    this.stateVars[stateVar].value = value;
  }

  getSupportedStateVars(): STATE_VAR[] {
    return this.getStateVar(STATE_VAR.SUPPORTED);
  }

  isStateVarSupported(stateVar: string): boolean {
    return this.getStateVar(STATE_VAR.SUPPORTED).includes(stateVar);
  }

  getUserAttribute(userAttribute: string): any {
    return this.userAttributes[userAttribute];
  }

  setUserAttribute(userAttribute: string, value: any): void {
    this.userAttributes[userAttribute] = value;
  }

  setName(name: string) {
    return (this.name = name);
  }

  getType(): string {
    let type;

    this.getGroups().forEach((group) => {
      if (
        DeviceTypesMap.map.get(DeviceType.CLIMATIZATION).groups.includes(group)
      ) {
        type = DeviceType.CLIMATIZATION;
      } else if (
        DeviceTypesMap.map.get(DeviceType.SENSORS).groups.includes(group)
      ) {
        type = DeviceType.SENSORS;
      } else if (
        DeviceTypesMap.map.get(DeviceType.GENERATION).groups.includes(group)
      ) {
        type = DeviceType.GENERATION;
      } else if (
        DeviceTypesMap.map.get(DeviceType.CONSUMPTION).groups.includes(group)
      ) {
        type = type === DeviceType.GENERATION ? type : DeviceType.CONSUMPTION;
      }
    });
    return type;
  }

  getSubType(): string {
    let subType;
    if (this.getType() === DeviceType.CLIMATIZATION) {
      this.getGroups().forEach((group) => {
        if (
          ClimatizationDeviceTypesMap.map
            .get(ClimatizationDeviceType.TEMPERATURE)
            .groups.includes(group)
        ) {
          subType = ClimatizationDeviceType.TEMPERATURE;
        } else if (
          ClimatizationDeviceTypesMap.map
            .get(ClimatizationDeviceType.THERMOSTAT)
            .groups.includes(group)
        ) {
          subType = ClimatizationDeviceType.THERMOSTAT;
        } else if (
          ClimatizationDeviceTypesMap.map
            .get(ClimatizationDeviceType.AIR_CONDITIONING)
            .groups.includes(group)
        ) {
          subType = ClimatizationDeviceType.AIR_CONDITIONING;
        }
      });
    } else if (this.getType() === DeviceType.SENSORS) {
    }
    return subType;
  }

  isConnectivityStateOnline(): boolean {
    return this.getConnectivityState() === CONNECTIVITY_STATE.ONLINE;
  }

  isConnectivityStateOffline(): boolean {
    return this.getConnectivityState() === CONNECTIVITY_STATE.OFFLINE;
  }

  isConnectivityStateSleeping(): boolean {
    return this.getConnectivityState() === CONNECTIVITY_STATE.SLEEPING;
  }

  isIconRed(): boolean {
    return (
      this.isConnectivityStateOnline() &&
      (this.isStateVarSupported(STATE_VAR.TEMPERATURE) ||
        (this.isStateVarSupported(STATE_VAR.PROCESS_TEMP) &&
          this.getStateVar(STATE_VAR.SETPOINT) !== 7))
    );
  }

  isIconYellow(): boolean {
    return this.isConnectivityStateSleeping();
  }

  isDefaultTemperature(): boolean {
    return (
      this.isStateVarSupported(STATE_VAR.TEMPERATURE) ||
      this.isStateVarSupported(STATE_VAR.PROCESS_TEMP)
    );
  }

  isHvacOff(): boolean {
    return (
      this.isConnectivityStateOffline() ||
      this.getStateVar(STATE_VAR.OPERATION_MODE) === "OFF"
    );
  }

  getHistoricVars(): Map<string, any> {
    return this.historicVars;
  }

  getPower(): number {
    return SharedUtils.getEnergy(
      this.isStateVarSupported("activePowerAplus")
        ? this.getStateVar("activePowerAplus").value
        : this.getStateVar("activePowerAminus").value
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/devices/devices.model_new.ts